/**
 * Comprehensive input validation utilities for the Enhanced Assessment Platform
 * Provides validation functions for email, required fields, length constraints, numeric ranges,
 * and input sanitization to prevent XSS attacks
 */

// Basic validation functions
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export const validateRequired = (value) => {
  return value !== null && value !== undefined && value.toString().trim() !== '';
};

export const validateMinLength = (value, minLength) => {
  if (!value) return false;
  return value.toString().length >= minLength;
};

export const validateMaxLength = (value, maxLength) => {
  if (!value) return true;
  return value.toString().length <= maxLength;
};

export const validateNumber = (value, min = null, max = null) => {
  const num = Number(value);
  if (isNaN(num)) return false;
  if (min !== null && num < min) return false;
  if (max !== null && num > max) return false;
  return true;
};

export const validateInteger = (value, min = null, max = null) => {
  const num = Number(value);
  if (isNaN(num) || !Number.isInteger(num)) return false;
  if (min !== null && num < min) return false;
  if (max !== null && num > max) return false;
  return true;
};

export const validatePercentage = (value) => {
  return validateNumber(value, 0, 100);
};

export const validateUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Enhanced input sanitization to prevent XSS attacks
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return String(input || '');
  
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .trim();
};

// Advanced HTML sanitization for rich text content
export const sanitizeHtml = (input) => {
  if (typeof input !== 'string') return String(input || '');
  
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframe tags
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '') // Remove object tags
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '') // Remove embed tags
    .replace(/<link\b[^<]*(?:(?!<\/link>)<[^<]*)*<\/link>/gi, '') // Remove link tags
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '') // Remove style tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') // Remove event handlers
    .trim();
};

// Validate array of strings
export const validateStringArray = (array, minItems = 0, maxItems = null) => {
  if (!Array.isArray(array)) return false;
  if (array.length < minItems) return false;
  if (maxItems !== null && array.length > maxItems) return false;
  return array.every(item => typeof item === 'string' && item.trim() !== '');
};

// Validate date string (ISO 8601 format)
export const validateDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && dateString === date.toISOString();
};

// Validate time duration in minutes
export const validateDuration = (minutes) => {
  return validateInteger(minutes, 1, 600); // 1 minute to 10 hours
};

// Form field validation helper with detailed error messages
export const validateField = (value, rules) => {
  const errors = [];
  
  if (rules.required && !validateRequired(value)) {
    errors.push('This field is required');
  }
  
  if (value && rules.minLength && !validateMinLength(value, rules.minLength)) {
    errors.push(`Minimum length is ${rules.minLength} characters`);
  }
  
  if (value && rules.maxLength && !validateMaxLength(value, rules.maxLength)) {
    errors.push(`Maximum length is ${rules.maxLength} characters`);
  }
  
  if (value && rules.email && !validateEmail(value)) {
    errors.push('Please enter a valid email address');
  }
  
  if (value && rules.number) {
    const { min, max } = rules.number;
    if (!validateNumber(value, min, max)) {
      const minText = min !== null ? min : 'any';
      const maxText = max !== null ? max : 'any';
      errors.push(`Number must be between ${minText} and ${maxText}`);
    }
  }
  
  if (value && rules.integer) {
    const { min, max } = rules.integer;
    if (!validateInteger(value, min, max)) {
      const minText = min !== null ? min : 'any';
      const maxText = max !== null ? max : 'any';
      errors.push(`Must be a whole number between ${minText} and ${maxText}`);
    }
  }
  
  if (value && rules.url && !validateUrl(value)) {
    errors.push('Please enter a valid URL');
  }
  
  if (value && rules.custom && !rules.custom(value)) {
    errors.push(rules.customMessage || 'Invalid value');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    error: errors[0] || null // First error for simple display
  };
};

// Question validation with comprehensive error reporting
export const validateQuestion = (question) => {
  const errors = {};
  
  // Validate question ID
  if (!validateRequired(question.id)) {
    errors.id = 'Question ID is required';
  } else if (!validateMinLength(question.id, 1)) {
    errors.id = 'Question ID must be at least 1 character';
  } else if (!validateMaxLength(question.id, 50)) {
    errors.id = 'Question ID must not exceed 50 characters';
  }
  
  // Validate question text
  if (!validateRequired(question.question)) {
    errors.question = 'Question text is required';
  } else if (!validateMinLength(question.question, 10)) {
    errors.question = 'Question must be at least 10 characters';
  } else if (!validateMaxLength(question.question, 1000)) {
    errors.question = 'Question must not exceed 1000 characters';
  }
  
  // Validate assessment name
  if (!validateRequired(question.assessment_name)) {
    errors.assessment_name = 'Assessment name is required';
  } else if (!validateMinLength(question.assessment_name, 1)) {
    errors.assessment_name = 'Assessment name must be at least 1 character';
  } else if (!validateMaxLength(question.assessment_name, 100)) {
    errors.assessment_name = 'Assessment name must not exceed 100 characters';
  }
  
  // Validate category
  if (!validateRequired(question.category)) {
    errors.category = 'Category is required';
  } else if (!validateMinLength(question.category, 1)) {
    errors.category = 'Category must be at least 1 character';
  } else if (!validateMaxLength(question.category, 100)) {
    errors.category = 'Category must not exceed 100 characters';
  }
  
  // Validate choices array
  if (!question.choices || !Array.isArray(question.choices)) {
    errors.choices = 'Choices must be an array';
  } else if (question.choices.length < 2) {
    errors.choices = 'At least 2 choices are required';
  } else if (question.choices.length > 6) {
    errors.choices = 'Maximum 6 choices allowed';
  } else {
    // Validate individual choices
    const choiceErrors = [];
    question.choices.forEach((choice, index) => {
      if (!validateRequired(choice)) {
        choiceErrors.push(`Choice ${index + 1} cannot be empty`);
      } else if (!validateMaxLength(choice, 500)) {
        choiceErrors.push(`Choice ${index + 1} must not exceed 500 characters`);
      }
    });
    
    if (choiceErrors.length > 0) {
      errors.choices = choiceErrors.join('; ');
    }
    
    // Check for duplicate choices
    const uniqueChoices = new Set(question.choices.map(c => c.trim().toLowerCase()));
    if (uniqueChoices.size !== question.choices.length) {
      errors.choices = 'All choices must be unique';
    }
  }
  
  // Validate correct answer
  if (!validateRequired(question.correct_answer)) {
    errors.correct_answer = 'Correct answer is required';
  } else if (question.choices && !question.choices.includes(question.correct_answer)) {
    errors.correct_answer = 'Correct answer must be one of the choices';
  }
  
  // Validate difficulty (optional field)
  if (question.difficulty !== undefined && question.difficulty !== null) {
    if (!validateInteger(question.difficulty, 1, 5)) {
      errors.difficulty = 'Difficulty must be an integer between 1 and 5';
    }
  }
  
  // Validate tags (optional field)
  if (question.tags !== undefined) {
    if (!validateStringArray(question.tags, 0, 10)) {
      errors.tags = 'Tags must be an array of strings (maximum 10)';
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    errorCount: Object.keys(errors).length
  };
};

// Assessment validation with enhanced checks
export const validateAssessment = (assessment) => {
  const errors = {};
  
  // Validate assessment name
  if (!validateRequired(assessment.name)) {
    errors.name = 'Assessment name is required';
  } else if (!validateMinLength(assessment.name, 3)) {
    errors.name = 'Assessment name must be at least 3 characters';
  } else if (!validateMaxLength(assessment.name, 100)) {
    errors.name = 'Assessment name must not exceed 100 characters';
  }
  
  // Validate title (optional)
  if (assessment.title && !validateMaxLength(assessment.title, 200)) {
    errors.title = 'Title must not exceed 200 characters';
  }
  
  // Validate description (optional)
  if (assessment.description && !validateMaxLength(assessment.description, 1000)) {
    errors.description = 'Description must not exceed 1000 characters';
  }
  
  // Validate total questions
  if (!validateInteger(assessment.totalQuestions, 1, 100)) {
    errors.totalQuestions = 'Total questions must be an integer between 1 and 100';
  }
  
  // Validate time limit
  if (!validateInteger(assessment.timeLimit, 5, 300)) {
    errors.timeLimit = 'Time limit must be an integer between 5 and 300 minutes';
  }
  
  // Validate passing score
  if (assessment.passingScore !== undefined) {
    if (!validateInteger(assessment.passingScore, 0, 100)) {
      errors.passingScore = 'Passing score must be an integer between 0 and 100';
    }
  }
  
  // Validate shuffle settings
  if (assessment.shuffleQuestions !== undefined && typeof assessment.shuffleQuestions !== 'boolean') {
    errors.shuffleQuestions = 'Shuffle questions must be a boolean value';
  }
  
  if (assessment.shuffleChoices !== undefined && typeof assessment.shuffleChoices !== 'boolean') {
    errors.shuffleChoices = 'Shuffle choices must be a boolean value';
  }
  
  // Validate category distribution
  if (assessment.distribution) {
    if (typeof assessment.distribution !== 'object' || Array.isArray(assessment.distribution)) {
      errors.distribution = 'Distribution must be an object';
    } else {
      const categories = Object.keys(assessment.distribution);
      const percentages = Object.values(assessment.distribution);
      
      // Check if all percentages are valid numbers
      const invalidPercentages = percentages.filter(p => !validatePercentage(p));
      if (invalidPercentages.length > 0) {
        errors.distribution = 'All distribution percentages must be between 0 and 100';
      } else {
        // Check if percentages sum to 100
        const total = percentages.reduce((sum, p) => sum + p, 0);
        if (Math.abs(total - 100) > 0.01) {
          errors.distribution = `Category distribution must total 100% (currently ${total.toFixed(1)}%)`;
        }
      }
      
      // Validate category names
      const invalidCategories = categories.filter(cat => !validateRequired(cat) || !validateMaxLength(cat, 100));
      if (invalidCategories.length > 0) {
        errors.distribution = 'All category names must be non-empty and not exceed 100 characters';
      }
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    errorCount: Object.keys(errors).length
  };
};

// Program validation
export const validateProgram = (program) => {
  const errors = {};
  
  // Validate program name
  if (!validateRequired(program.name)) {
    errors.name = 'Program name is required';
  } else if (!validateMinLength(program.name, 3)) {
    errors.name = 'Program name must be at least 3 characters';
  } else if (!validateMaxLength(program.name, 100)) {
    errors.name = 'Program name must not exceed 100 characters';
  }
  
  // Validate title
  if (!validateRequired(program.title)) {
    errors.title = 'Program title is required';
  } else if (!validateMinLength(program.title, 5)) {
    errors.title = 'Program title must be at least 5 characters';
  } else if (!validateMaxLength(program.title, 200)) {
    errors.title = 'Program title must not exceed 200 characters';
  }
  
  // Validate description
  if (!validateRequired(program.description)) {
    errors.description = 'Program description is required';
  } else if (!validateMinLength(program.description, 10)) {
    errors.description = 'Program description must be at least 10 characters';
  } else if (!validateMaxLength(program.description, 1000)) {
    errors.description = 'Program description must not exceed 1000 characters';
  }
  
  // Validate assessment name
  if (!validateRequired(program.assessment_name)) {
    errors.assessment_name = 'Associated assessment is required';
  }
  
  // Validate status
  const validStatuses = ['active', 'inactive'];
  if (!program.status || !validStatuses.includes(program.status)) {
    errors.status = 'Status must be either "active" or "inactive"';
  }
  
  // Validate requirements (optional)
  if (program.requirements !== undefined) {
    if (!validateStringArray(program.requirements, 0, 20)) {
      errors.requirements = 'Requirements must be an array of strings (maximum 20)';
    }
  }
  
  // Validate learning platform URL (optional)
  if (program.learning_platform_url && !validateUrl(program.learning_platform_url)) {
    errors.learning_platform_url = 'Learning platform URL must be a valid URL';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    errorCount: Object.keys(errors).length
  };
};

// User enrollment validation
export const validateUserEnrollment = (enrollment) => {
  const errors = {};
  
  // Validate user email
  if (!validateRequired(enrollment.user_email)) {
    errors.user_email = 'User email is required';
  } else if (!validateEmail(enrollment.user_email)) {
    errors.user_email = 'Valid email address is required';
  }
  
  // Validate program name
  if (!validateRequired(enrollment.program_name)) {
    errors.program_name = 'Program name is required';
  }
  
  // Validate enrollment date
  if (!validateRequired(enrollment.enrollment_date)) {
    errors.enrollment_date = 'Enrollment date is required';
  } else if (!validateDate(enrollment.enrollment_date)) {
    errors.enrollment_date = 'Enrollment date must be a valid ISO 8601 date';
  }
  
  // Validate status
  const validStatuses = ['active', 'inactive', 'completed'];
  if (!enrollment.status || !validStatuses.includes(enrollment.status)) {
    errors.status = 'Status must be one of: active, inactive, completed';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    errorCount: Object.keys(errors).length
  };
};

// Exam attempt validation
export const validateExamAttempt = (attempt) => {
  const errors = {};
  
  // Validate user email
  if (!validateRequired(attempt.user_email)) {
    errors.user_email = 'User email is required';
  } else if (!validateEmail(attempt.user_email)) {
    errors.user_email = 'Valid email address is required';
  }
  
  // Validate assessment name
  if (!validateRequired(attempt.assessment_name)) {
    errors.assessment_name = 'Assessment name is required';
  }
  
  // Validate timestamp
  if (!validateRequired(attempt.timestamp)) {
    errors.timestamp = 'Timestamp is required';
  } else if (!validateDate(attempt.timestamp)) {
    errors.timestamp = 'Timestamp must be a valid ISO 8601 date';
  }
  
  // Validate score
  if (!validateNumber(attempt.score, 0, 100)) {
    errors.score = 'Score must be a number between 0 and 100';
  }
  
  // Validate correct answers count
  if (!validateInteger(attempt.correct, 0)) {
    errors.correct = 'Correct answers must be a non-negative integer';
  }
  
  // Validate total questions count
  if (!validateInteger(attempt.total, 1)) {
    errors.total = 'Total questions must be a positive integer';
  }
  
  // Validate that correct <= total
  if (attempt.correct !== undefined && attempt.total !== undefined && attempt.correct > attempt.total) {
    errors.correct = 'Correct answers cannot exceed total questions';
  }
  
  // Validate passed status
  if (attempt.passed !== undefined && typeof attempt.passed !== 'boolean') {
    errors.passed = 'Passed status must be a boolean value';
  }
  
  // Validate time spent (optional)
  if (attempt.time_spent !== undefined && !validateInteger(attempt.time_spent, 0)) {
    errors.time_spent = 'Time spent must be a non-negative integer (seconds)';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    errorCount: Object.keys(errors).length
  };
};

// Batch validation utility
export const validateBatch = (items, validator) => {
  const results = items.map(validator);
  const isValid = results.every(result => result.isValid);
  const totalErrors = results.reduce((sum, result) => sum + (result.errorCount || 0), 0);
  
  return { 
    isValid, 
    results, 
    totalErrors,
    validCount: results.filter(r => r.isValid).length,
    invalidCount: results.filter(r => !r.isValid).length
  };
};